/**
 * F2用 Firebase認証機能
 * GoogleログインとAppleログインの実装
 */

import { loginWithGooglePopup, loginWithApplePopup, logout, loginWithCustomToken } from '../firebase/auth';
import { auth, storage } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, getBlob } from "firebase/storage";

// ページ読み込み時の処理
document.addEventListener('turbo:load', () => {
  console.log('F2 Firebase認証: ページ読み込み完了');
  // Firebase認証状態の変更を監視
  onAuthStateChanged(auth, (user) => {
    console.log('user', user);
    if (user) {
      console.log('Firebase認証状態変更: ログイン中', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });
    } else {
      console.log('Firebase認証状態変更: ログアウト状態');
    }
  });
  // ログインボタン・ログアウトリンクを設定
  setupAuthButtons();
  // カスタムトークンでのログインをチェック
  checkCustomTokenLogin().then((result) => {
    console.log('checkCustomTokenLogin完了');
    //firebase画像の表示
    loadImagesFromFirebaseStorage();
  });
});

// turbo streamの監視
// ページ内のDOM変更を監視するためのMutationObserverを設定
const observer = new MutationObserver((mutationsList) => {
  for (const mutation of mutationsList) {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.querySelectorAll) {
        // サムネイル画像の読み込み
        if (node.querySelectorAll('span.firebase-lazy-load[data-path]').length > 0) {
          loadImagesFromFirebaseStorage();
        }
      }
    });
  }
});
observer.observe(document.body, {
  childList: true,       // 子ノードの追加・削除
  attributes: false,     // 属性の変更を監視したいなら true
  subtree: true          // 子孫ノードも含めて監視
});


/**
 * firabaseのストレージから画像のパスを取得して読み込む
 */
async function loadImagesFromFirebaseStorage() {
  const placeholders = document.querySelectorAll('span.firebase-lazy-load');


  for (const span of placeholders) {
    const path = span.dataset.path;
    if (!path) continue;

    // すでに処理済みの場合はスキップ
    if (span.dataset.loading) continue;
    span.dataset.loading = 'true';

    try{
      const storageRef = ref(storage, path);
      const blob = await getBlob(storageRef);
      const url = URL.createObjectURL(blob);
      const img = document.createElement('img');
      // 画像の読み込み完了を待つ
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });

      // spanのすべての属性をimgにコピー
      Array.from(span.attributes).forEach(attr => {
        if (attr.name !== 'data-path' && 
            attr.name !== 'data-loading' &&
            !attr.name.includes('firebase-lazy-load') && 
            attr.name !== 'class') {
          img.setAttribute(attr.name, attr.value);
        }
      });

      // spanのクラス（firebase-lazy-load以外）をimgにコピー
      span.classList.forEach(className => {
        if (className !== 'firebase-lazy-load' && className !== 'loading' && 
            className !== 'loading-spinner' && className !== 'loading-xl') {
          img.classList.add(className);
        }
      });

      // spanをimgに置き換え（spanはDOMから削除される）
      span.replaceWith(img);

      window.addEventListener('beforeunload', () => {
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error(`画像取得失敗:`, error);
      delete span.dataset.loading;
    }
  }
}


/**
 * カスタムトークンによるfirebaseログイン
 */
async function checkCustomTokenLogin() {
  // hidden inputからカスタムトークンを取得
  let customToken = null;
  const hiddenToken = document.getElementById('firebase-custom-token');
  if (hiddenToken) {
    customToken = hiddenToken.value;
  }
  if (customToken) {
    console.log('カスタムトークンでのログインを検出');
    // カスタムトークンでログイン
    await loginWithCustomToken(customToken);
  }
}

/**
 * 招待トークンを取得
 * URLから招待トークンを取得するか、hidden inputから取得
 * @returns {string|null} 招待トークン
 */
function getInvitationToken() {
  // URLからinvitation_tokenパラメータを取得
  const urlParams = new URLSearchParams(window.location.search);
  let token = urlParams.get('invitation_token');

  // URLにない場合はhidden inputから取得
  if (!token) {
    const tokenInput = document.querySelector('input[name="f2_user[invitation_token]"]');
    token = tokenInput ? tokenInput.value : null;
  }

  return token;
}

/**
 * ログインボタンとログアウトリンクの設定
 */
function setupAuthButtons() {
  // ログインページのGoogleログインボタン処理
  setupLoginButton();

  // 登録ページのGoogleログインボタン処理
  setupSignupButton();

  // ログインページのAppleログインボタン処理
  setupAppleLoginButton();

  // 登録ページのAppleログインボタン処理
  setupAppleSignupButton();

  // ログアウトリンクの処理
  setupLogoutHandler();
}

/**
 * Googleログインボタンの設定
 */
function setupLoginButton() {
  const googleLoginButton = document.getElementById('google-login');
  if (googleLoginButton) {
    console.log('Googleログインボタンを設定します');

    googleLoginButton.addEventListener('click', async () => {
      try {
        // エラーメッセージをクリア
        hideError();

        console.log('Googleログインを開始します');
        const { user, provider, error } = await loginWithGooglePopup();

        // エラー処理
        if (error) {
          console.error('ログインエラー:', error);
          showError(`ログイン中にエラーが発生しました: ${error}`);
          return;
        }

        // 認証成功
        if (user) {
          console.log('ログイン成功:', user.email);
          // ログインモードを指定して認証成功処理を実行
          await handleAuthSuccess(user, 'login', provider);
        }
      } catch (error) {
        // 未処理のエラー
        console.error('ログイン処理中のエラー:', error);
        showError(`予期しないエラーが発生しました: ${error.message}`);
      }
    });
  }
}

/**
 * Appleログインボタンの設定
 */
function setupAppleLoginButton() {
  const appleLoginButton = document.getElementById('apple-login');
  if (appleLoginButton) {
    console.log('Appleログインボタンを設定します');

    appleLoginButton.addEventListener('click', async () => {
      try {
        // エラーメッセージをクリア
        hideError();

        console.log('Appleログインを開始します');
        const { user, provider, error } = await loginWithApplePopup();

        // エラー処理
        if (error) {
          console.error('ログインエラー:', error);
          showError(`ログイン中にエラーが発生しました: ${error}`);
          return;
        }

        // 認証成功
        if (user) {
          console.log('ログイン成功:', user.email);
          // ログインモードを指定して認証成功処理を実行
          await handleAuthSuccess(user, 'login', provider);
        }
      } catch (error) {
        // 未処理のエラー
        console.error('ログイン処理中のエラー:', error);
        showError(`予期しないエラーが発生しました: ${error.message}`);
      }
    });
  }
}

/**
 * Google登録ボタンの設定
 */
function setupSignupButton() {
  const googleSignupButton = document.getElementById('google-signup');
  if (googleSignupButton) {
    console.log('Google登録ボタンを設定します');

    googleSignupButton.addEventListener('click', async () => {
      try {
        // エラーメッセージをクリア
        hideError();

        console.log('Google登録を開始します');
        const { user, provider, error } = await loginWithGooglePopup();

        // エラー処理
        if (error) {
          console.error('登録エラー:', error);
          showError(`登録中にエラーが発生しました: ${error}`);
          return;
        }

        // 認証成功
        if (user) {
          console.log('登録成功:', user.email);
          // 登録モードを指定して認証成功処理を実行
          const invitationToken = getInvitationToken();
          console.log('招待トークン:', invitationToken);
          await handleAuthSuccess(user, 'signup', provider, invitationToken);
        }
      } catch (error) {
        // 未処理のエラー
        console.error('登録処理中のエラー:', error);
        showError(`予期しないエラーが発生しました: ${error.message}`);
      }
    });
  }
}

/**
 * Apple登録ボタンの設定
 */
function setupAppleSignupButton() {
  const appleSignupButton = document.getElementById('apple-signup');
  if (appleSignupButton) {
    console.log('Apple登録ボタンを設定します');

    appleSignupButton.addEventListener('click', async () => {
      try {
        // エラーメッセージをクリア
        hideError();

        console.log('Apple登録を開始します');
        const { user, provider, error } = await loginWithApplePopup();

        // エラー処理
        if (error) {
          console.error('登録エラー:', error);
          showError(`登録中にエラーが発生しました: ${error}`);
          return;
        }

        // 認証成功
        if (user) {
          console.log('登録成功:', user.email);
          // 登録モードを指定して認証成功処理を実行
          const invitationToken = getInvitationToken();
          console.log('招待トークン:', invitationToken);
          await handleAuthSuccess(user, 'signup', provider, invitationToken);
        }
      } catch (error) {
        // 未処理のエラー
        console.error('登録処理中のエラー:', error);
        showError(`予期しないエラーが発生しました: ${error.message}`);
      }
    });
  }
}

/**
 * ログアウトボタンの処理設定
 */
function setupLogoutHandler() {
  // ログアウトフォームを検索（button_toで生成されるフォーム）
  const logoutForm = document.querySelector('form[action*="sign_out"]');
  if (logoutForm) {
    // submitイベントをインターセプト
    logoutForm.addEventListener('submit', async (e) => {
      // フォーム送信を一時停止
      e.preventDefault();
      try {
        // Firebaseからログアウト
        await auth.signOut();
      } catch (error) {
        console.error('Firebaseログアウトエラー:', error);
      } finally {
        // フォーム送信を実行（Railsセッションのログアウト）
        e.target.submit();
      }
    });
  } else {
    console.warn('ログアウトフォームが見つかりませんでした');
  }
}

/**
 * 認証成功時の処理
 * @param {Object} user - Firebase認証ユーザーオブジェクト
 * @param {String} authMode - 認証モード（'login'または'signup'）
 * @param {String} provider - 認証プロバイダー（'google'または'apple'）
 * @param {String} invitationToken - 招待トークン（招待の場合のみ）
 */
async function handleAuthSuccess(user, authMode, provider, invitationToken = null) {
  try {
    console.log(`認証成功の処理を開始します（モード: ${authMode}, プロバイダー: ${provider}, 招待: ${invitationToken ? 'あり' : 'なし'}）`);

    // サーバーにトークンを送信
    await sendTokenToServer(user, authMode, provider, invitationToken);

    return true;
  } catch (error) {
    console.error('認証成功処理中のエラー:', error);
    showError(`認証エラー: ${error.message}`);
    return false;
  }
}

/**
 * ユーザートークンをサーバーに送信
 * @param {Object} user - Firebase認証ユーザーオブジェクト
 * @param {String} authMode - 認証モード（'login'または'signup'）
 * @param {String} provider - 認証プロバイダー（'google'または'apple'）
 * @param {String} invitationToken - 招待トークン（招待の場合のみ）
 */
async function sendTokenToServer(user, authMode, provider, invitationToken = null) {
  try {
    console.log('ユーザートークンを取得します:', user.email);
    const token = await user.getIdToken();

    // CSRFトークンを取得
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (!csrfToken) {
      throw new Error('CSRF tokenが見つかりません');
    }

    console.log(`サーバーにトークンを送信します（モード: ${authMode}, プロバイダー: ${provider}, 招待: ${invitationToken ? 'あり' : 'なし'}）`);
    const response = await fetch('/f2/auth/firebase_callback', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ 
        token,
        auth_mode: authMode,
        auth_provider: provider,
        invitation_token: invitationToken,
        timestamp: new Date().getTime() // キャッシュ防止用
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('サーバーエラー:', errorData);
      throw new Error(errorData.message || `サーバーでの認証に失敗しました (${response.status})`);
    }

    const data = await response.json();
    console.log('認証成功レスポンス:', data);

    if (data.status === 'success') {
      // 認証成功後、F2用のログインページにリダイレクト
      window.location.href = '/f2/auth/firebase_login';
    }
  } catch (error) {
    console.error('トークン送信エラー:', error);
    showError(error.message);
    throw error;
  }
}

/**
 * エラーメッセージの表示
 */
function showError(message) {
  const errorElements = document.querySelectorAll('.error-message');
  errorElements.forEach(el => {
    el.textContent = message;
    el.style.display = 'block';
  });
}

/**
 * エラーメッセージの非表示
 */
function hideError() {
  const errorElements = document.querySelectorAll('.error-message');
  errorElements.forEach(el => {
    el.style.display = 'none';
  });
}
